import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import useCreateAudit from "../../mutation/audit/createAudit";

import moment from "moment";
import Swal from "sweetalert2";

import DataTable from "react-data-table-component";
import Toast from "../../utils/Toast";

import { AuthContext } from "../../utils/userProvider";
import Loader from "../../components/Loader";
import useRateNumbers from "../../queries/rateNumber/getRateNumbers";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const RateNumbers = () => {
  const [promotionTable, setPromotionTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);
  const [auditResponse, setAuditData] = useCreateAudit();

  const [openDeleteRateNumber, setOpenDeleteRateNumber] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  const typeToText = (type) => {
    let text = "";
    switch (type) {
      case "promotional_period":
        text = "Promotional Period";
        break;
      case "weekly_rent_rebate":
        text = "Weekly Rent Rebate";
        break;
      case "hk_rate_override":
        text = "HK Rate Override";
        break;
      default:
        break;
    }
    return text;
  };

  let columns = [
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "Code",
      selector: "code",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{row.code}</span>;
      },
    },
    {
      name: "Start Date",
      selector: "startDate",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{moment(row.startDate).format("DD MMM YYYY")}</span>;
      },
    },
    {
      name: "End Date",
      selector: "endDate",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{moment(row.endDate).format("DD MMM YYYY")}</span>;
      },
    },
    {
      name: "Property",
      sortable: false,
      cell: (row, index, column, id) => {
        return <span>{row.prop.name}</span>;
      },
    },
    {
      name: "Options",
      cell: (row, index, column, id) => {
        if (authState.editAccess) {
          if (!row.archived) {
            return (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={() => {
                    editItem(row);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    deleteItem(row);
                  }}
                >
                  Delete
                </Button>
              </div>
            );
          } else {
            return null;
          }
        } else {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  editItem(row);
                }}
              >
                View
              </Button>
            </div>
          );
        }
      },
    },
  ];

  const [rateNumberResponse, resetRateNumber] = useRateNumbers();

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = promotionTable.filter(
    (item) =>
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.type &&
        typeToText(item.type)
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.startDate &&
        moment(item.startDate)
          .format("DD MMM YYYY")
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.endDate &&
        moment(item.endDate)
          .format("DD MMM YYYY")
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.property &&
        item.property.name.toLowerCase().includes(filterText.toLowerCase()))
  );

  const updateTableData = (data) => {
    let newTableData = [...promotionTable];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setPromotionTable(newTableData);
    } else {
      setPromotionTable(newTableData);
    }
  };

  useEffect(() => {
    if (rateNumberResponse.data) {
      setPromotionTable([...rateNumberResponse.data]);
    }
  }, [rateNumberResponse]);

  const editItem = (row) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/rate-numbers/edit/" + row.id;
    } else {
      window.location.href = "/dashboard/rate-numbers/view/" + row.id;
    }
  };

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeleteRateNumber(true);
  };

  const handleClose = () => {
    setOpenDeleteRateNumber(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
            <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "rate-number",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Rate Number deletion Audit Created",
      });
      setDeleteRow(null);
      setOpenDeleteRateNumber(false);
      resetRateNumber();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeleteRateNumber(false);
    }
  }, [auditResponse]);

  const newRateNumber = () => {
    window.location.href = "/dashboard/rate-numbers/new";
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        {authState.role == "Executive" && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={newRateNumber}
          >
            New Rate Number
          </Button>
        )}
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  return (
    <ListPageShell>
      <ListPageCard>
        {rateNumberResponse.loading && <Loader />}
        {!rateNumberResponse.loading && (
          <div>
            <ListPageHeader title="Rate Numbers" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        open={openDeleteRateNumber}
        onClose={(event, reason) => {
          if (
            auditResponse.loading &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus
        sx={modalSx}
      >
        <Fade in={openDeleteRateNumber}>
          <Box>
            <DeleteModalFrame title="Delete Rate Number">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="secondary"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default RateNumbers;
